import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Alert, Box, Paper, Typography } from '@mui/material'
import { EditOutlined, KeySharp, ToggleOnOutlined, UnfoldLessOutlined, UnfoldMoreOutlined } from '@mui/icons-material'
import { CommerceTree, CustomButton, FilterBar } from 'components'
import DialogModal from 'components/modal/DialogModal'
import { confirmAlert, errorAlert } from 'helpers/alerts'
import { validatePermission } from 'helpers/authentication'
import { getCommerceTreeService, updateCommerceStateService } from 'services/client/commerceService'
import { recoverPassword, resetPassword } from 'services/security/userServices'
import { setCurrentCommerce } from 'redux/slices/client/commerceSlice'
import { setTitle } from 'redux/slices/uiSlice'

const styles = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' },
  buttons: { display: 'flex', gap: 1, flexWrap: 'wrap' },
  frame: { p: 2, mt: 2 },
  hint: { color: 'text.secondary' },
}

const ERROR_CARGA = 'No se pudo cargar la estructura de comercios.'

// Permiso propio de esta pantalla, no el general de comercios: la vista enseña la
// red entera y sólo la abren los roles a los que se les concede este permiso.
const CATEGORIA = 'comercios'
const PERMISO = 'árbol de comercios'

// El detalle del error tampoco se da por bueno: cuando la API no responde, lo que
// llega es la pagina de error del proxy entera. Solo se muestra un mensaje corto
// y sin marcas, y en cualquier otro caso se deja el texto fijo.
const mensajeDeError = (e) => {
  const detalle = typeof e === 'string' ? e : e?.message
  const util = typeof detalle === 'string' &&
    detalle.trim() &&
    detalle.trim().length <= 120 &&
    !detalle.includes('<')

  return util ? `${ERROR_CARGA} ${detalle.trim()}` : ERROR_CARGA
}

const CommerceTreeScreen = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { hoster } = useSelector(store => store.session)

  const [comercios, setComercios] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filters, setFilters] = useState({})
  const [expanded, setExpanded] = useState({})
  const [dialog, setDialog] = useState({ open: false })

  const puedeVer = validatePermission(CATEGORIA, PERMISO, ['ver'])

  useEffect(() => {
    dispatch(setTitle('Árbol de Comercios'))
    // eslint-disable-next-line
  }, [])

  // Depende del permiso, no sólo del montaje: nada más iniciar sesión los permisos
  // todavía no han llegado del servidor, y con [] el árbol no se pedía nunca.
  useEffect(() => {
    if (!puedeVer) {
      setLoading(false)
      return
    }

    setLoading(true)
    getCommerceTreeService()
      .then((response) => {
        // Lo que llega del backend no se da por bueno: se acepta el objeto y
        // tambien un array pelado, por si el core va por delante o por detras
        const list = Array.isArray(response?.comercios) ? response.comercios
          : Array.isArray(response) ? response
            : null

        if (!list) {
          setError(ERROR_CARGA)
          setComercios([])
          return
        }
        setComercios(list)
      })
      .catch((e) => setError(mensajeDeError(e)))
      .finally(() => setLoading(false))
    // eslint-disable-next-line
  }, [puedeVer])

  const handleToggle = (node) => {
    const id = node?._id
    if (!id) return

    setExpanded(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const handleExpandAll = () => setExpanded(
    comercios.reduce((acc, item) => (item?._id ? { ...acc, [String(item._id)]: true } : acc), {})
  )

  const handleCollapseAll = () => setExpanded({})

  const handlerEdit = (node) => {
    dispatch(setCurrentCommerce({
      ...node,
      type_bag: Array.isArray(node.bags) ? node.bags.map(bag => bag.type) : [],
    }))
    navigate('/cliente/comercios/editar')
  }

  const handlerResetPassword = (node) => {
    const email = node?.user?.email
    const id = node?.user?._id
    const sendEmail = node?.type_commerce !== 'H2H'

    confirmAlert({
      title: `¿Estas seguro que deseas restablecer la contraseña del usuario ${email}?`,
      text: sendEmail
        ? `Se enviará al usuario un correo electrónico con un enlace para restablecer`
        : `La contraseña se actulizará y mostrará la nueva contraseña solo una vez.`,
      onConfirm: () => {
        if (sendEmail)
          recoverPassword(email).then(() => {
            confirmAlert({
              icon: 'success',
              title: `¡Correo enviado exitosamente!`,
              showCancelButton: false,
            })
          }).catch((e) => errorAlert(e))
        else
          resetPassword(id).then(({ password }) => {
            confirmAlert({
              icon: 'success',
              title: `¡Se restablecio la contraseña exitosamente!`,
              html: `<b>Contraseña: </b> ${password}`,
              showCancelButton: false,
            })
          }).catch((e) => errorAlert(e))
      },
    })
  }

  const handlerState = (node) => {
    const value = !node.state
    setDialog({
      open: true,
      title: value ? '¿Activar este comercio?' : '¿Desactivar este comercio?',
      body: `El comercio ${node.username || 'sin nombre'} quedará ${value ? 'activo' : 'inactivo'}.`,
      onSuccess: () => {
        setDialog({ open: false })
        updateCommerceStateService(node._id, { state: value })
          .then(() => setComercios(prev => prev.map(item => (
            String(item._id) === String(node._id) ? { ...item, state: value } : item
          ))))
          .catch((e) => errorAlert(e))
      },
      onClose: () => setDialog({ open: false }),
    })
  }

  const actions = useMemo(() => ([
    validatePermission(CATEGORIA, PERMISO, ['editar']) && {
      label: 'Restablecer contraseña',
      icon: <KeySharp fontSize="small" />,
      onClick: handlerResetPassword,
      // Sin usuario no hay contraseña que restablecer
      validate: (node) => Boolean(node?.user?.email) && String(node?._id) !== String(hoster?.id),
    },
    validatePermission(CATEGORIA, PERMISO, ['editar']) && {
      label: 'Activar / desactivar',
      icon: <ToggleOnOutlined fontSize="small" />,
      onClick: handlerState,
      validate: (node) => typeof node?.state === 'boolean' && String(node?._id) !== String(hoster?.id),
    },
    validatePermission(CATEGORIA, PERMISO, ['editar']) && {
      label: 'Editar comercio',
      icon: <EditOutlined fontSize="small" />,
      onClick: handlerEdit,
      validate: (node) => String(node?._id) !== String(hoster?.id),
    },
  ].filter(item => item)),
    // eslint-disable-next-line
    [hoster, comercios])

  if (!puedeVer)
    return <Alert severity="warning">No tienes permiso para ver el árbol de comercios.</Alert>

  return (
    <>
      <FilterBar
        handlerFilter={(fields) => setFilters({
          search: fields?.search,
          // Por defecto sólo los activos; el check los deja ver todos
          state: fields?.inactivos ? undefined : true,
          type: fields?.type || undefined,
        })}
        defaultValues={{ search: '', inactivos: false, type: '' }}
        filters={[
          { name: 'search', label: 'Buscar (nombre, id o correo)', type: 'text' },
          {
            name: 'type',
            label: 'Tipo',
            type: 'select',
            data: [
              { name: 'Todos' },
              { id: 'H2H', name: 'H2H' },
              { id: 'DISTRIBUIDOR', name: 'Distribuidor' },
              { id: 'MENUDEO', name: 'Menudeo' },
            ],
          },
          { name: 'inactivos', label: 'Ver inactivos', type: 'checkbox' },
        ]}
      />

      <Paper sx={styles.frame}>
        <Box sx={styles.header}>
          <Typography variant="body2" sx={styles.hint}>
            {comercios.length} {comercios.length === 1 ? 'comercio' : 'comercios'} en la estructura
          </Typography>
          <Box sx={styles.buttons}>
            <CustomButton
              dark={true}
              icon={<UnfoldMoreOutlined />}
              name="Expandir todo"
              click={handleExpandAll}
            />
            <CustomButton
              dark={true}
              icon={<UnfoldLessOutlined />}
              name="Contraer todo"
              click={handleCollapseAll}
            />
          </Box>
        </Box>

        <Box mt={2}>
          <CommerceTree
            comercios={comercios}
            loading={loading}
            error={error}
            filters={filters}
            expanded={expanded}
            actions={actions}
            onToggle={handleToggle}
          />
        </Box>
      </Paper>

      <DialogModal
        open={dialog.open}
        title={dialog.title}
        body={dialog.body}
        onSuccess={dialog.onSuccess}
        onClose={dialog.onClose}
      />
    </>
  )
}

export default CommerceTreeScreen
